import React, { useState } from "react";
import { Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { QrCodeImage } from "./QrCodeImage";
import { BarcodeImage } from "./BarcodeImage";
import { downloadBarcodePng, downloadQrPng } from "../utils/codeImages";

interface CodeCardProps {
  title: string;
  subtitle: string;
  qrValue?: string;
  barcodeValue?: string;
  badge: string;
  badgeColor: string;
  downloadName: string;
}

export function CodeCard({
  title,
  subtitle,
  qrValue,
  barcodeValue,
  badge,
  badgeColor,
  downloadName,
}: CodeCardProps) {
  const [copied, setCopied] = useState(false);
  const qrText = qrValue?.trim() ?? "";
  const barcodeText = barcodeValue?.trim() ?? "";

  const handleCopy = async (value: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQr = async () => {
    if (!qrText) {
      toast.error("QR-код не задан");
      return;
    }
    try {
      await downloadQrPng(qrText, downloadName);
      toast.success("QR-код сохранён");
    } catch {
      toast.error("Не удалось скачать QR");
    }
  };

  const handleDownloadBarcode = () => {
    if (!barcodeText) {
      toast.error("Штрихкод не задан");
      return;
    }
    try {
      downloadBarcodePng(barcodeText, downloadName);
      toast.success("Штрихкод сохранён");
    } catch {
      toast.error("Не удалось скачать штрихкод");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
      <div className="flex items-start gap-4">
        <div className="flex flex-col gap-2 flex-shrink-0">
          <QrCodeImage value={qrText} size={100} />
          {barcodeText && <BarcodeImage value={barcodeText} className="w-[100px]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full mb-2 ${badgeColor}`}>
            {badge}
          </div>
          <h3 className="text-[14px] text-gray-900">{title}</h3>
          <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">{subtitle}</p>
          <div className="font-mono text-[10px] text-gray-300 mt-2 truncate" title={qrText || undefined}>
            QR: {qrText || "—"}
          </div>
          {barcodeText && (
            <div className="font-mono text-[10px] text-gray-300 mt-0.5 truncate" title={barcodeText}>
              Штрихкод: {barcodeText}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void handleCopy(qrText)}
          disabled={!qrText}
          className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 rounded-xl border border-gray-200 text-[12px] text-gray-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Скопировано" : "Копировать"}
        </button>
        <button
          type="button"
          onClick={() => void handleDownloadQr()}
          className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-[12px] text-indigo-600 transition-colors"
        >
          <Download size={13} /> QR PNG
        </button>
        {barcodeText && (
          <button
            type="button"
            onClick={handleDownloadBarcode}
            className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-[12px] text-emerald-700 transition-colors"
          >
            <Download size={13} /> Штрихкод PNG
          </button>
        )}
      </div>
    </div>
  );
}
